import { Fields as CouponPool } from 'models/CouponPool'
import { Fields as MerchantFields } from 'models/Merchant'
import { Fields as RedeemTemplate } from 'models/RedeemTemplate'
import { RedemptionProvider } from 'models/RedemptionProvider'
import { generateFormModel, sameIds } from '../../helpers'
import { RedeemTypeItem } from '../../types'
import { castFormModel } from './castFormModel'
import { FormModel } from './index'

/**
 * Pure function that transforms a form model when the redeem type changes.
 * This encapsulates the complete type change logic in a testable way.
 */
export function transformFormModelForTypeChange(
  newType: RedeemTypeItem,
  merchant: MerchantFields | null,
  template?: RedeemTemplate,
  couponPools?: CouponPool[] | null,
  redemptionProviders?: RedemptionProvider[]
): FormModel {
  // Rebuild the current form model from template
  const currentFormModel = generateFormModel(
    merchant,
    template,
    couponPools,
    redemptionProviders
  )

  // Strip out all properties irrelevant to the new type
  const newFormModel = castFormModel({
    ...currentFormModel,
    typeItem: newType,
  })

  // Create a default form model to backfill defaults for any missing fields
  const defaultFormModel = generateFormModel(
    merchant,
    undefined,
    couponPools,
    redemptionProviders
  )

  // Backfill any missing fields with defaults
  backfillMissingFields(newFormModel, defaultFormModel)
  backfillPosFields(newFormModel, defaultFormModel)

  // Apply type-specific configuration overrides
  applyTypeSpecificOverrides(newFormModel, defaultFormModel, newType.key)

  // Reset pricing and cost fields to default values
  resetDiscountAndCost(newFormModel, defaultFormModel)

  // Every type override above lands on a scope without modifier discounts, so
  // drop any carried-over lists; otherwise a later prefill would keep them.
  return clearModifierDiscounts(newFormModel)
}

function backfillMissingFields(
  targetModel: FormModel,
  defaultModel: FormModel
): void {
  // Re-populate cleaned-up model with default values
  // This is necessary because some parts of the codebase aren't properly type-checked
  Object.keys(defaultModel).forEach(key => {
    if (targetModel[key] === undefined) {
      targetModel[key] = defaultModel[key]
    }
  })
}

function backfillPosFields(
  targetModel: FormModel,
  defaultModel: FormModel
): void {
  // Put back default values for unused POS fields
  if (targetModel.posFields !== undefined) {
    Object.keys(targetModel.posFields).forEach(pos => {
      const posField = targetModel.posFields[pos]
      const defaultPosField = defaultModel.posFields[pos]

      Object.keys(defaultPosField).forEach(field => {
        if (posField[field] === undefined) {
          targetModel.posFields[pos][field] = defaultPosField[field]
        }
      })
    })
  }
}

function applyTypeSpecificOverrides(
  model: FormModel,
  defaultModel: FormModel,
  typeKey: string
): void {
  // Type-specific default overrides not handled by generateFormModel
  if (typeKey === 'golden_ticket') {
    model.availableCustomerSupport = false
    model.availableInstore = true
    model.availableOnline = false
  }

  if (typeKey === 'hidden_menu') {
    model.availableCustomerSupport = false
  }

  if (typeKey === 'any_modifier_for_$x') {
    model.priceCap = defaultModel.priceCap
    model.discountScope = 'required_modifiers'
  }

  if (typeKey === 'any_item_for_$x') {
    model.priceCap = defaultModel.priceCap
    model.discountScope = 'base_item'
  }

  if (typeKey === 'free_fixed_price_item') {
    model.priceCap = '0'
    model.discountScope = 'free_base_item'
  }

  if (typeKey === 'delivery_discount') {
    model.deliveryFeeMode = 'amount'
    model.discountedCheckSection = 'delivery_fee'
    model.priceCap = ''
    model.discount = ''
  }
}

function resetDiscountAndCost(model: FormModel, defaultModel: FormModel): void {
  model.discount = defaultModel.discount
  model.perceivedValue = defaultModel.perceivedValue
}

const seededIds = (
  modifierDiscountIds: string[] | undefined,
  restrictionIds: string[] | undefined
): string[] =>
  modifierDiscountIds?.length
    ? modifierDiscountIds
    : [...(restrictionIds ?? [])]

/**
 * Prefills each venue's empty modifier discounts from that venue's modifier
 * restrictions, so choosing an "Item + modifier discounts" scope starts in the
 * default mode where both lists match. A venue that already has a different
 * list keeps it and opens in advanced mode.
 *
 * Provider copies are seeded in-store only and only for multi-POS merchants:
 * that is the only case where posFields are edited (Configure POS). A
 * single-POS merchant edits the base fields, which merchant-api mirrors into
 * the provider copy, and online pricing reads the base digital list only.
 */
export function seedModifierDiscounts(
  model: FormModel,
  { seedProviders }: { seedProviders: boolean }
): FormModel {
  const posFields = seedProviders
    ? Object.fromEntries(
        Object.entries(model.posFields ?? {}).map(([provider, field]) => {
          const instoreIds = seededIds(
            field.instoreModifierDiscountIds,
            field.instoreDiscountedModifierIds
          )
          return [
            provider,
            {
              ...field,
              instoreModifierDiscountIds: instoreIds,
              instoreModifierDiscountsAdvanced: !sameIds(
                instoreIds,
                field.instoreDiscountedModifierIds
              ),
            },
          ]
        })
      )
    : model.posFields
  const instoreIds = seededIds(
    model.instoreModifierDiscountIds,
    model.instoreDiscountedModifierIds
  )
  const onlineIds = seededIds(
    model.onlineModifierDiscountIds,
    model.onlineDiscountedModifierIds
  )

  return {
    ...model,
    instoreModifierDiscountIds: instoreIds,
    onlineModifierDiscountIds: onlineIds,
    instoreModifierDiscountsAdvanced: !sameIds(
      instoreIds,
      model.instoreDiscountedModifierIds
    ),
    onlineModifierDiscountsAdvanced: !sameIds(
      onlineIds,
      model.onlineDiscountedModifierIds
    ),
    posFields,
  }
}

/** Empties every modifier discount list (base and per POS provider). */
export function clearModifierDiscounts(model: FormModel): FormModel {
  const posFields = Object.fromEntries(
    Object.entries(model.posFields ?? {}).map(([provider, field]) => [
      provider,
      {
        ...field,
        instoreModifierDiscountIds: [],
        onlineModifierDiscountIds: [],
        instoreModifierDiscountsAdvanced: false,
      },
    ])
  )

  return {
    ...model,
    instoreModifierDiscountIds: [],
    onlineModifierDiscountIds: [],
    instoreModifierDiscountsAdvanced: false,
    onlineModifierDiscountsAdvanced: false,
    posFields,
  }
}
